import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronsRight, X } from "lucide-react";

import TableSkeleton from "../components/TableSkeleton";
import SortableTableHeader from "../components/SortableTableHeader";
import DownloadExcel from "../components/DownloadExcel";
// Utils
import { updateSearchParams } from "../utils/commonMethods";
import TablePagination from "./TablePagination";

const ModernServerTable = ({
  rows = [],
  title,
  isExport,
  exportApi,
  exportParams,
  exportModule,
  totalPages,
  isDelete,
  primaryActionButtons = null,
  secondaryActionButtons = null,
  turneryActionButtons = null,
  headerActionButtons = null,
  headerActionButtonsPosition = "top",
  headerStyle = undefined,
  titleAccentColor = undefined,
  searchTrailingContent = null,
  onBack = null,
  backButtonLabel = "Back",
  isFetching = false,
  columns = [],
  totalItems,
  pageSize,
  onPageChange,
  currentPage,
  onPageSizeChange,
  pageSizeOptions,
  rowId = null,
  hideCheckBox = false,
  hideTableTopActionPanel = false,
  hideTableBottomPagination = false,
  hideTableTopSearchInput = false,
  onSearchChange = () => {},
  onDeleteSelected = () => {},
  rowSelectionModel,
  onRowSelectionModelChange = () => {},
  showGridLines = false,
}) => {
  // Search params
  // const [searchParams, setSearchParams] = useSearchParams();

  // Pagination
  // const [currentPage, setCurrentPage] = useState(
  //   parseInt(searchParams.get("page")) || 1,
  // );
  const currentItems = rows;

  const location = useLocation();
  const tableScrollRef = useRef(null);

  const scrollKey = `table-scroll-${location.pathname}`;
  const searchStorageKey = `table-search-${location.pathname}`;

  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Filters & Search
  const [searchValue, setSearchValue] = useState(
    () => sessionStorage.getItem(searchStorageKey) || "",
  );

  useEffect(() => {
    setSearchValue(sessionStorage.getItem(searchStorageKey) || "");
  }, [searchStorageKey]);

  // Selection
  const [selectedLeads, setSelectedLeads] = useState([]);
  const isSelectionControlled = Array.isArray(rowSelectionModel);
  const selectedRowIds = isSelectionControlled ? rowSelectionModel : selectedLeads;
  const updateSelection = useCallback(
    (nextSelection) => {
      if (!isSelectionControlled) setSelectedLeads(nextSelection);
      onRowSelectionModelChange(nextSelection);
    },
    [isSelectionControlled, onRowSelectionModelChange],
  );
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Debounce search
  useEffect(() => {
    const debounceTime = setTimeout(() => {
      onSearchChange(searchValue);
    }, 500);
    return () => clearTimeout(debounceTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, onSearchChange]);

  const handleSearchChange = (value) => {
    setSearchValue(value);

    if (value) {
      sessionStorage.setItem(searchStorageKey, value);
    } else {
      sessionStorage.removeItem(searchStorageKey);
    }
  };

  useEffect(() => {
    const container = tableScrollRef.current;

    if (!container) return;

    const savedScroll = sessionStorage.getItem(scrollKey);

    if (savedScroll) {
      setTimeout(() => {
        container.scrollTop = Number(savedScroll);
      }, 100);
    }
  }, [scrollKey]);

  useEffect(() => {
    const container = tableScrollRef.current;

    if (!container) return;

    const saveScroll = () => {
      sessionStorage.setItem(scrollKey, container.scrollTop);
    };

    container.addEventListener("scroll", saveScroll);

    return () => {
      container.removeEventListener("scroll", saveScroll);
    };
  }, [scrollKey]);

  const isSomeSelected = useMemo(
    () =>
      selectedRowIds.length > 0 &&
      selectedRowIds.length < currentItems.length,
    [selectedRowIds, currentItems],
  );

  // Sorting
  const sortedItems = useMemo(() => {
    if (!sortConfig?.key) return rows;

    return [...rows].sort((a, b) => {
      let aValue = a?.[sortConfig.key];
      let bValue = b?.[sortConfig.key];

      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [rows, sortConfig]);

  const handleSelectItem = useCallback(
    (id, checked) => {
      const nextSelection = checked
        ? [...new Set([...selectedRowIds, id])]
        : selectedRowIds.filter((item) => item !== id);
      updateSelection(nextSelection);
    },
    [selectedRowIds, updateSelection],
  );

  const handleSelectAll = useCallback(
    (checked) => {
      // If some (but not all) are selected, always clear
      if (isSomeSelected) {
        updateSelection([]);
        return;
      }
      const updated = checked
        ? currentItems.map((lead) => lead[rowId || "_id"])
        : [];
      updateSelection(updated);
    },
    [currentItems, rowId, isSomeSelected, updateSelection],
  );

  const isAllSelected = useMemo(
    () =>
      currentItems.length > 0 &&
      currentItems.every((item) =>
        selectedRowIds.includes(item[rowId || "_id"]),
      ),
    [currentItems, selectedRowIds, rowId],
  );

  // Sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    // Note: If API supports sorting, update params here
  };

  const getSortIcon = useCallback(
    (columnKey) => {
      if (sortConfig.key !== columnKey) {
        return <ArrowUpDown className="w-3 h-3" />;
      }
      return sortConfig.direction === "asc" ? (
        <ArrowUp className="w-3 h-3" />
      ) : (
        <ArrowDown className="w-3 h-3" />
      );
    },
    [sortConfig],
  );

  // ✅ Fixed getStickyStyle
  const getStickyStyle = (col) => {
    const base = {
      minWidth: col.minWidth ? `${col.minWidth}px` : undefined,
      maxWidth: col.maxWidth ? `${col.maxWidth}px` : undefined,
    };

    if (!col.sticky) return base;

    return col.stickySide === "right"
      ? { ...base, right: col.stickyOffset ?? 0 }
      : { ...base, left: col.stickyOffset ?? 0 };
  };

  // ✅ Fixed getStickyClass — always includes bg
  const getStickyClass = (col) => {
    const base = "bg-gradient-to-r from-[#f9fbfd] to-white";

    if (!col.sticky) return base;

    return col.stickySide === "right"
      ? `${base} sticky z-20 shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.08)]`
      : `${base} sticky z-20 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]`;
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Main Content */}
      <main className="flex-1 overflow-auto ">
        {/* data Listing Card */}
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-[calc(100vh-5.4rem)] backdrop-blur-sm">
          {headerActionButtonsPosition === "top" && headerActionButtons}
          <div
            className="flex rounded-xl items-center justify-between p-6 border-b border-gray-200/60 flex-shrink-0 bg-gradient-to-r from-white to-[#f9fbfd]/30 gap-4 flex-wrap"
            style={headerStyle}
          >
            <h3 style={{ textTransform: "capitalize" }} className="text-[#2e67be] flex items-center gap-2 mr-auto">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  title={backButtonLabel}
                  aria-label={backButtonLabel}
                  className="p-1 -ml-1 rounded-md text-[#2e67be] hover:bg-[#eef4ff] transition-colors"
                >
                  <ChevronsRight className="w-5 h-5 rotate-180" strokeWidth={2.5} />
                </button>
              )}
              <div
                className="w-1 h-6 bg-gradient-to-b from-[#2e67be] to-[#4985f2] rounded-full"
                style={titleAccentColor ? { background: titleAccentColor } : undefined}
              ></div>
              {title}
            </h3>

            {/* Search and Advanced Search */}
            {!hideTableTopSearchInput && (
              <div className="flex items-center gap-2 bg-white/50 backdrop-blur-sm px-3 py-2 rounded-lg border border-gray-200/60 shadow-sm focus-within:shadow-md focus-within:border-[#2e67be]/30 transition-all duration-300 w-full md:w-auto">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  className="bg-transparent border-none outline-none text-sm text-gray-700 w-full md:w-64 placeholder:text-gray-400"
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {searchValue && (
                  <button
                    type="button"
                    onClick={() => handleSearchChange("")}
                    className="text-gray-400 hover:text-gray-700 transition-colors"
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {searchTrailingContent}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Export */}
              {isExport && (
                <DownloadExcel
                  api={exportApi}
                  params={exportParams}
                  module={exportModule}
                />
              )}

              {/* Delete */}
              {isDelete && (
                <button
                  onClick={onDeleteSelected}
                  className="px-3 py-2 rounded-lg bg-gradient-to-br from-[#f9fbfd] to-white border border-[#739fe1]/20 flex items-center gap-2 hover:bg-white hover:shadow-lg hover:border-[#2e67be]/30 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete selected leads permanently"
                  disabled={selectedRowIds.length === 0}
                >
                  <Trash2 className="w-4 h-4 text-[#739fe1] group-hover:text-[#2e67be] transition-colors" />
                  <span className="text-sm text-[#739fe1] group-hover:text-[#2e67be] transition-colors">
                    Delete
                  </span>
                </button>
              )}
              {primaryActionButtons}

              {/* Divider */}
              {secondaryActionButtons && (
                <div className="w-px h-6 bg-gray-300 mx-1"></div>
              )}
              {turneryActionButtons}
              {secondaryActionButtons}
            </div>
          </div>
          {headerActionButtonsPosition === "bottom" && headerActionButtons}

          {/* Table with horizontal and vertical scroll */}
          <div
            ref={tableScrollRef}
            className={`flex-1 overflow-auto ${showGridLines ? "border border-gray-300" : ""}`}
          >
            {isFetching ? (
              <TableSkeleton rows={10} columns={columns.length || 6} />
            ) : (
              <table className="w-full">
                <thead className="sticky top-0 z-30 border-b border-gray-200/60">
                  <tr>
                    {!hideCheckBox && (
                      <th className="text-left px-6 py-4 sticky left-0 bg-gradient-to-r from-[#f9fbfd] to-white z-30">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-[#2e67be] focus:ring-[#2e67be] focus:ring-2 transition-all cursor-pointer"
                          checked={isAllSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = isSomeSelected;
                          }}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                    )}
                    {columns?.map((col) =>
                      col.isSortable == false ? (
                        <th
                          className={`text-center px-6 py-4 text-sm text-gray-600 whitespace-nowrap ${
                            showGridLines
                              ? "border-r border-b border-gray-300"
                              : ""
                          } ${getStickyClass(col)} ${col.className || ""}`}
                          style={getStickyStyle(col)}
                        >
                          {col.headerName}
                        </th>
                      ) : (
                        <SortableTableHeader
                          key={col.field}
                          label={col.headerName}
                          columnKey={col.field}
                          sortIcon={getSortIcon(col.field)}
                          onSort={requestSort}
                          className={`text-center px-6 py-4 text-sm text-gray-600 whitespace-nowrap ${getStickyClass(col)} `}
                          style={getStickyStyle(col)}
                        />
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems?.length ? (
                    sortedItems.map((record) => {
                      const isDuplicate =
                        record.isDuplicate === true || record.enquiries?.length > 1;
                      return (
                        <tr
                          key={record[rowId || "_id"]}
                          className={`group/row border-b border-gray-100/60 transition-colors duration-200 ${isDuplicate ? "bg-amber-50 hover:bg-amber-100/70" : "hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white"}`}
                        >
                        {/* ✅ checkbox column */}
                        {!hideCheckBox && (
                          <td
                            className={`px-6 py-4 sticky left-0 z-10 ${isDuplicate ? "bg-amber-50" : "bg-white"}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300 text-[#2e67be]"
                              // checked={rowSelectionModel.includes(record[rowId || "_id"])}
                              checked={selectedRowIds.includes(
                                record[rowId || "_id"],
                              )}
                              onChange={(e) =>
                                handleSelectItem(
                                  record[rowId || "_id"],
                                  e.target.checked,
                                )
                              }
                            />
                          </td>
                        )}

                        {/* ✅ dynamic columns */}
                        {columns.map((col) => {
                          const value = col.renderCell
                            ? col.renderCell({ row: record })
                            : col.valueFormatter
                              ? col.valueFormatter({
                                  value: record[col.field],
                                })
                              : col.valueGetter
                                ? col.valueGetter({ row: record })
                                : record[col.field];

                          return (
                            <td
                              key={col.field}
                              className={`px-6 py-4 text-sm text-center truncate ${
                                showGridLines ? "border-b border-gray-300" : ""
                              } ${
                                col.sticky
                                  ? `sticky left-0 z-10 ${isDuplicate ? "bg-amber-50" : "bg-gradient-to-r from-[#f9fbfd] to-white"}`
                                  : ""
                              } ${col.className || ""}`}
                              title={typeof value === "string" ? value : ""}
                              style={getStickyStyle(col)}
                            >
                              {value}
                            </td>
                          );
                        })}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-6 py-8 text-center text-gray-400"
                      >
                        "No data found"
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {!hideTableBottomPagination && (
            <TablePagination
              totalItems={totalItems}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={onPageChange}
              currentPage={currentPage}
              onPageSizeChange={onPageSizeChange}
              pageSizeOptions={pageSizeOptions}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default ModernServerTable;
